import LinkNoPrefetch from '@/components/link-noprefetch';
import Image from 'next/image';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';

const capitalizeFirst = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str);

const extractBlockEditorText = (blocks) => {
    if (!blocks?.content) return null;
    const texts = [];
    const walk = (nodes) => {
        for (const node of nodes || []) {
            if (node.type === 'text' && node.text) texts.push(node.text);
            if (node.content) walk(node.content);
        }
    };
    walk(blocks.content);
    return texts.join(' ').trim() || null;
};

export default function IntegrationCard({ integration, contentType }) {
    if (contentType === 'Company') {
        const {
            title,
            type1,
            urlTitle,
            identifier,
            logo,
            hidePage,
            website1,
            thumbnailAlt,
            address,
            category,
            teaser
        } = integration;

        const categoryLabel = typeof category === 'string' ? category : category?.selectValue;
        const addressText = extractBlockEditorText(address);
        const href = hidePage ? website1 || '#' : `/ecosystem/partner/${urlTitle}`;

        return (
            <LinkNoPrefetch
                href={href}
                className="flex flex-col rounded-lg border border-gray-300 bg-white transition-shadow hover:shadow-md">
                {logo && (
                    <div className="flex items-center justify-center px-6 py-12">
                        <div className="relative h-[75px] w-4/5">
                            <Image
                                src={`/dA/${identifier}/image`}
                                alt={thumbnailAlt || title}
                                fill
                                sizes="75px"
                                className="object-contain"
                            />
                        </div>
                    </div>
                )}
                <div className="flex flex-1 flex-col border-t border-gray-200 p-6">
                    <div className="mb-3 flex flex-wrap gap-2">
                        <span className="rounded-full bg-fuschia-200 px-2.5 py-0.5 text-xs font-medium text-fuschia-900">
                            Partner
                        </span>
                        {type1 && (
                            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                                {type1 === 'partner' ? 'Solution' : capitalizeFirst(type1)}
                            </span>
                        )}
                        {type1 === 'partner' && addressText && (
                            <span
                                className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700"
                                title={addressText.length > 50 ? addressText : undefined}>
                                {addressText.length > 50
                                    ? `${addressText.slice(0, 50)}…`
                                    : addressText}
                            </span>
                        )}
                        {type1 === 'technology' && categoryLabel && (
                            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                                {categoryLabel}
                            </span>
                        )}
                    </div>
                    <h3 className="mb-1 text-base font-semibold text-gray-900">
                        {capitalizeFirst(title)}
                    </h3>
                    {teaser && (
                        <DotBlockEditor
                            blocks={teaser}
                            customRenderers={{
                                // Override `paragraph`, not `text`: the SDK already wraps text
                                // nodes in its own <p>, so styling at the `text` level nested
                                // <p> inside <p> and broke hydration.
                                paragraph: ({ children }) => (
                                    <p className="prose prose-sm my-0 max-w-none text-gray-600">
                                        {children}
                                    </p>
                                )
                            }}
                        />
                    )}
                </div>
            </LinkNoPrefetch>
        );
    }

    const { title, tagline, badge, category, slug, identifier, logo } = integration;
    const badgeLabel = typeof badge === 'string' ? badge : badge?.selectValue;
    const categoryLabel = typeof category === 'string' ? category : category?.selectValue;

    return (
        <LinkNoPrefetch
            href={`/ecosystem/${slug}`}
            className="flex flex-col rounded-lg border border-gray-300 bg-white transition-shadow hover:shadow-lg">
            {logo && (
                <div className="flex items-center justify-center px-6 py-12">
                    <div className="relative h-[75px] w-full">
                        <Image
                            src={`/dA/${identifier}/image`}
                            alt={title}
                            fill
                            sizes="75px"
                            className="object-contain"
                        />
                    </div>
                </div>
            )}
            <div className="flex flex-1 flex-col border-t border-gray-200 p-6">
                <div className="mb-3 flex flex-wrap gap-2">
                    <span className="rounded-full bg-indigo-200 px-2.5 py-0.5 text-xs font-medium text-indigo-900">
                        Integration
                    </span>
                    {badgeLabel && (
                        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                            {badgeLabel}
                        </span>
                    )}
                    {categoryLabel && (
                        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                            {categoryLabel}
                        </span>
                    )}
                </div>
                <h3 className="mb-1 text-base font-semibold text-gray-900">
                    {capitalizeFirst(title)}
                </h3>
                {tagline && (
                    <p className="prose prose-sm my-0 max-w-none text-gray-600">{tagline}</p>
                )}
            </div>
        </LinkNoPrefetch>
    );
}
